import cn from 'classnames';
import {
  Dispatch,
  SetStateAction,
  memo,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'next-i18next';

import { IconButton } from 'components';
import { AccomplishmentResponseAttributes } from 'lib/models/candidate';
import { useAuth } from 'lib/providers/AuthProvider';

import { getSkillsInfo } from '../utils';

import styles from './DisplaySkills.module.scss';

interface ProfileModalProps {
  isEdit?: boolean;
  openModal?: () => void;
  payload?: AccomplishmentResponseAttributes[];
  setPayload?: Dispatch<SetStateAction<AccomplishmentResponseAttributes[]>>;
}

export const DisplaySkills = memo(
  ({ isEdit, openModal, payload, setPayload }: ProfileModalProps) => {
    const { t } = useTranslation('candidate.profile.modal');
    const { candidate } = useAuth();

    const [accomplishmentsInfo, setAccomplishmentsInfo] = useState<
      (AccomplishmentResponseAttributes & {
        isNew: boolean;
      })[]
    >([]);

    useEffect(() => {
      if (candidate && !isEdit) {
        setAccomplishmentsInfo(getSkillsInfo(candidate));
      } else if (isEdit && payload) {
        const accomplishments = payload.map((item) => {
          return { ...item, isNew: false };
        });
        setAccomplishmentsInfo(accomplishments);
      }
    }, [candidate, payload]);

    const deleteAccomplishment = async (index: number, type: string) => {
      if (setPayload && payload) {
        let position = 0;
        const existingAccomplishment = payload.filter((item, i) => {
          if (item.title === type) {
            position = i;
            return true;
          }
        });

        const newPayload = structuredClone(payload);
        if (existingAccomplishment.length) {
          newPayload[position].values.splice(index, 1);
        }
        setPayload(newPayload);
      }
    };

    const createSection = (item: { title: string; values: string[] }) => {
      return (
        <section className={styles.section}>
          <div className={styles.titleBox}>
            <p className={styles.title}>{item.title}</p>
            {!isEdit && (
              <IconButton
                iconName="icon_pencil-filled"
                size="xsmallplus"
                onClick={openModal}
              />
            )}
          </div>
          <div className={styles.badgeBox}>
            {item.values.map((value, index) => (
              <div key={value} className={styles.badge}>
                <span>{value}</span>
                {isEdit && (
                  <IconButton
                    iconName="close"
                    onClick={() => deleteAccomplishment(index, item.title)}
                    size="xsmall"
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      );
    };

    const createEmptySection = (title: string) => {
      return (
        <section className={styles.section}>
          <p className={styles.title}>{title}</p>
          <span className={styles.emptyState}>
            {t('skills.empty-state', { type: title.toLocaleLowerCase() })}
          </span>
        </section>
      );
    };
    const getSkills = useMemo(() => {
      const skills = accomplishmentsInfo.filter(
        (item) => item.title === t('skills.dropdown-options.skills')
      );
      if (skills.length === 0 || skills[0].values.length === 0)
        return createEmptySection(t('skills.dropdown-options.skills'));
      return createSection(skills[0]);
    }, [accomplishmentsInfo]);

    const getAchievements = useMemo(() => {
      const achievements = accomplishmentsInfo.filter(
        (item) => item.title === t('skills.dropdown-options.achievements')
      );
      if (achievements.length === 0 || achievements[0].values.length === 0)
        return createEmptySection(t('skills.dropdown-options.achievements'));
      return createSection(achievements[0]);
    }, [accomplishmentsInfo]);

    const getInterests = useMemo(() => {
      const interests = accomplishmentsInfo.filter(
        (item) => item.title === t('skills.dropdown-options.interests')
      );
      if (interests.length === 0 || interests[0].values.length === 0)
        return createEmptySection(t('skills.dropdown-options.interests'));
      return createSection(interests[0]);
    }, [accomplishmentsInfo]);

    return (
      <div className={cn(styles.container, { [styles.border]: isEdit })}>
        {getSkills}
        {getAchievements}
        {getInterests}
      </div>
    );
  }
);

DisplaySkills.displayName = 'DisplaySkills';
